/* eslint-disable react/jsx-no-undef */
import { FGCustomPanel, FGMultiSuggestInput, FGTextInput, FieldGroup, IDataTableColumn, Loading } from "nsitools-react";
import * as React from "react";
import { useParams } from "react-router";
import { ERoutes } from "../../../../ERoutes";
import {
  BreedGroupApi,
  PersonApi,
  BreedGroupsJudgeSaveDto,
  BreedGroupGridDto,
  BreedGroupSearch,
  CompetitionRequestApi,
} from "../../../../api";
import { SmallFormGenerator, SRSHCustomSelector } from "../../../../components";
import { useGlobalData } from "../../../../contexts";
import { useApiService, useTl, useCrudApi } from "../../../../hooks";
import { useReferential } from "../../../../hooks/useReferential";
import { ETLCodes } from "../../../../locales";

export interface ICompetitionBreedGroupPanelProps {}

export const CompetitionBreedGroupPanel: React.FunctionComponent<ICompetitionBreedGroupPanelProps> = ({}) => {
  const breedGroupApi = useApiService(BreedGroupApi);
  const competitionRequestApi = useApiService(CompetitionRequestApi);
  const { id, state } = useParams<{ id: string; state: string }>();
  const { t } = useTl();
  const { currentLanguage } = useGlobalData();

  const { data, loading, saveItem, saving } = useCrudApi(
    {
      getApiFn: async () => {
        const ids = await competitionRequestApi.getLinkedBreedGroupsIds(+id);
        return { breedGroupIds: ids };
      },
      saveApiFn: (d) => competitionRequestApi.saveCompetitionBreedGroups(+id, d.breedGroupIds),
      onSavedRoute: (d) => `${ERoutes.competitionRequest}/${+id}/breeds/edit`,
    },
    [id]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        header: () => t(ETLCodes.BreedGroupLabel),
        autoFitContent: true,
        fieldName: "name",
        render: (row: BreedGroupGridDto) => (
          <div>
            {row.breedGroupNumber}. {row.name}
          </div>
        ),
      },
    ],
    [t]
  );

  const searchBreedGroupIdsFunc = React.useCallback(
    (sObj) =>
      breedGroupApi.getIds(
        BreedGroupSearch.fromJS({
          ...sObj,
          languageId: currentLanguage.languageId,
        })
      ),
    [breedGroupApi, currentLanguage]
  );

  const searchBreedGroupFunc = React.useCallback(
    (sObj) =>
      breedGroupApi.baseSearch(
        BreedGroupSearch.fromJS({
          ...sObj,
          languageId: currentLanguage.languageId,
        })
      ),
    [breedGroupApi, currentLanguage]
  );

  return (
    <Loading isLoading={loading}>
      <SmallFormGenerator
        initialValues={data}
        onSubmit={saveItem}
        editMode={state === "edit"}
        loading={loading}
        saving={saving}
      >
        <FieldGroup columns={1} fieldsetProps={{ title: t(ETLCodes.BreedGroups) }}>
          <FGCustomPanel>
            {(ctx) => (
              <SRSHCustomSelector
                linkedIds={ctx.formik.values?.breedGroupIds}
                columns={columns}
                onLinkedIdsChanged={(ids) => ctx.formik.setFieldValue("breedGroupIds", ids)}
                idField="breedGroupId"
                searchFunction={searchBreedGroupFunc}
                searchIdsFunc={searchBreedGroupIdsFunc}
                editMode={state === "edit"}
              />
            )}
          </FGCustomPanel>
        </FieldGroup>
      </SmallFormGenerator>
    </Loading>
  );
};
